import { LogLevel, PassedInitialConfig } from 'angular-auth-oidc-client';
import { environment } from '../../environments/environment';

/**
 * OIDC client configuration for the Avalon IdP.
 *
 * Per-environment values (authority, API host, etc.) come from
 * `src/environments/environment.ts` — swapped at build time via the
 * `fileReplacements` block in angular.json.
 *
 * Redirect URLs are NOT in env files. They are derived at runtime from
 * `window.location.origin` so the same build works on localhost, UAT,
 * prod, and any future host
 * without env-file maintenance — the redirect URL is by definition
 * "back to wherever this SPA is currently running."
 *
 */
const origin = window.location.origin;

export const oidcConfig: PassedInitialConfig = {
  config: {
    authority: environment.authority,
    redirectUrl: `${origin}/auth/callback`,
    postLogoutRedirectUri: `${origin}/auth/logout`,
    clientId: environment.clientId,
    scope: environment.scope,
    responseType: 'code',
    silentRenew: true,
    useRefreshToken: true,
    renewTimeBeforeTokenExpiresInSeconds: 60,
    // Bearer header attached only on requests to these origins.
    secureRoutes: [ `${new URL(environment.apiBase).origin}/` ],
    // Avalon's OpenIddict server preserves the original `nonce` claim
    // when re-minting id_tokens on the refresh_token grant. The lib's
    // strict default compares that real nonce against its internal
    // `--RefreshToken--` sentinel and rejects the token. Setting this
    // flag tells the lib to skip nonce validation on refresh-flow
    // id_tokens (it still validates signature, issuer, audience, and
    // expiry). Spec basis: OIDC Core nonce-on-refresh is ambiguous —
    // see https://bitbucket.org/openid/connect/issues/1025.
    ignoreNonceAfterRefresh: true,
    // Force `client_id` into the /connect/endsession URL. The lib
    // identifies the client via `id_token_hint` by default and omits
    // client_id entirely. If logoff() is called while local storage
    // has no id_token (e.g. after a 401 cascade), Avalon receives an
    // end_session request with neither hint nor client_id, can't
    // identify the RP, drops the post_logout_redirect_uri, and strands
    // the user on /Identity/LoggedOut instead of /auth/logout. This
    // ensures Avalon can always identify the client and honour the
    // configured post_logout_redirect_uri.
    customParamsEndSessionRequest: { client_id: environment.clientId },
    logLevel: environment.production ? LogLevel.Warn : LogLevel.Debug,
  },
};

export const API_BASE = environment.apiBase;
